<script setup lang="ts">
import {computed} from "vue";

/**
 * The body mass index on its WHO scale: a bar split into the four classes,
 * with a marker where the patient stands. The class only names itself in the
 * tooltip — the position says it.
 */

const props = defineProps<{
    value: number | null;
    /**
     * The frozen rendering: a thinner, narrower bar that sits beside the value.
     */
    compact?: boolean;
}>();

/**
 * The bounds of the drawn scale — what lies beyond sits on the edge.
 */
const FLOOR = 15;
const CEILING = 40;

/**
 * The WHO classes, each ending at its ceiling, coloured as the Girerd
 * reading is: mint at normal, amber on either side, red from obesity on.
 */
const CLASSES: {ceiling: number; label: string; classes: string}[] = [
    {ceiling: 18.5, label: 'Insuffisance pondérale', classes: 'bg-orange/60'},
    {ceiling: 25, label: 'Corpulence normale', classes: 'bg-secondary'},
    {ceiling: 30, label: 'Surpoids', classes: 'bg-orange/60'},
    {ceiling: CEILING, label: 'Obésité', classes: 'bg-red-light/70'},
];

/**
 * The classes as segments of the bar, each as wide as its span of the scale.
 */
const segments = computed(() => {
    return CLASSES.map((reading, index) => ({
        ...reading,
        width: `${(reading.ceiling - (CLASSES[index - 1]?.ceiling ?? FLOOR)) / (CEILING - FLOOR) * 100}%`,
    }));
});

/**
 * The ticks between the classes, at their thresholds.
 */
const ticks = computed(() => {
    return CLASSES.slice(0, -1).map(reading => ({
        label: String(reading.ceiling).replace('.', ','),
        left: `${(reading.ceiling - FLOOR) / (CEILING - FLOOR) * 100}%`,
    }));
});

/**
 * Where the marker sits, clamped to the drawn scale.
 */
const position = computed(() => {
    if (props.value === null) {
        return null;
    }

    const clamped = Math.min(CEILING, Math.max(FLOOR, props.value));

    return `${(clamped - FLOOR) / (CEILING - FLOOR) * 100}%`;
});

const reading = computed(() => {
    return props.value === null ? null : CLASSES.find(current => props.value! < current.ceiling) ?? CLASSES[CLASSES.length - 1];
});
</script>

<template>
  <div
    class="flex flex-col"
    :class="compact ? 'gap-0.5 w-[96px]' : 'gap-1 w-[120px] pt-1'"
    :title="reading?.label"
  >
    <div class="relative flex gap-0.5 w-full" :class="compact ? 'h-1' : 'h-1.5'">
      <span
        v-for="segment in segments"
        :key="segment.label"
        class="h-full first:rounded-l-full last:rounded-r-full transition-opacity duration-300"
        :class="[segment.classes, {'opacity-35': reading && reading !== segment}]"
        :style="{width: segment.width}"
      />
      <span
        v-if="position"
        class="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white border-2 border-primary shadow-sm transition-[left] duration-300 ease-in-out-quint"
        :class="compact ? 'w-2.5 h-2.5' : 'w-3 h-3'"
        :style="{left: position}"
      />
    </div>

    <div class="relative w-full leading-none text-muted-foreground mt-1" :class="compact ? 'h-2 text-[8px]' : 'h-2.5 text-[9px]'">
      <span
        v-for="tick in ticks"
        :key="tick.label"
        class="absolute -translate-x-1/2"
        :style="{left: tick.left}"
      >
        {{ tick.label }}
      </span>
    </div>
  </div>
</template>
